import React from 'react'
import { PageProps, graphql } from 'gatsby'
import { Trans, useTranslation } from 'react-i18next'
import { MapIndexQuery } from '@graphql-types'
import MAPLayout from './_layout'
import { GatsbyImage } from 'gatsby-plugin-image'
import {
  FaCalendar,
  FaCalendarCheck,
  FaClock,
  FaMapMarkerAlt,
  FaPortrait,
  FaStar,
} from 'react-icons/fa'
import ReactMarkdown from 'react-markdown'
import moment from 'moment'
import { cx } from '@emotion/css'
import CourseButtons from '@components/CourseButtons'
import { css, useTheme } from '@emotion/react'
import FeaturedBy from '@components/FeaturedBy'
import CourseMAPNav from './_nav'
import { H2Highlight } from '@components/H2Highlight'
import CourseSummary from '@components/CourseSummary'
import { maxWidth, responsiveColumn } from 'src/themes'
import { ScrollAnimation } from '@components/ScrollAnimation'
import LiteYouTubeEmbed from 'react-lite-youtube-embed'
import 'react-lite-youtube-embed/dist/LiteYouTubeEmbed.css'
import CourseMAPLocator, { useCountry } from './_locator'

const MAPIndexPage = (props: PageProps<MapIndexQuery>) => {
  const [currentCountry] = useCountry()
  const { t } = useTranslation()
  const theme = useTheme()
  const today = moment()

  const course = props.data.course!
  const cohorts = course.cohorts?.filter(
    (val) =>
      moment(val?.value?.start_date, 'YYYY-MM-DD').isAfter(today) &&
      val?.value?.available_countries?.includes(currentCountry)
  )

  const nextCohort = cohorts?.[0]

  return (
    <MAPLayout course={course}>
      <div
        css={css`
					padding: 0 2rem;

					${maxWidth[2]} {
						padding: 0 0.5rem;
					}
				`}>
        <div
          css={css`
						line-height: 2rem;
					`}>
          <ReactMarkdown children={course.description!} />
        </div>
        <div
          css={css`
						margin: 2rem 0;
            display: flex;
            justify-content: center;
					`}>
          <CourseButtons
            course={course}
            openDay={props?.data.openDayEvent!}
            trial={
              currentCountry === 'hk'
                ? props?.data.trialLessonEvent!
                : props?.data.trialLessonEventUK!
            }
          />
        </div>
        <div
          css={css`
						display: flex;
						width: 100%;
						gap: 1rem;
						margin: 2rem 0;
					`}>
          <div>
            <GatsbyImage
              image={
                props.data.tqukLogo?.childImageSharp
                  ?.gatsbyImageData
              }
              alt={t('courses:map.tquk.endorsed_course')}
            />
          </div>
          <div
            css={css`
							display: flex;
							flex-direction: column;
							justify-content: center;
						`}>
            <h2
              css={css`
								color: ${theme.highlightColor};
								margin: 0;
							`}>
              {t('courses:map.tquk.level5_endorsed_course.title')}
            </h2>
            <p
              css={css`
								margin: 0;
							`}>
              {t(
                'courses:map.tquk.level5_endorsed_course.description'
              )}
            </p>
          </div>
        </div>
        <div
          css={css`
						position: relative;
						width: 100%;
						height: 0;
						padding-bottom: 56.25%;
						margin-bottom: 1.5rem;

						iframe {
							position: absolute;
							top: 0;
							left: 0;
							width: 100%;
							height: 100%;
						}

						button {
							border: none;
						}
					`}>
          <LiteYouTubeEmbed
            id='qFzIy9Lxhnk'
            title={t('courses:map.youtube_title')}
          />
        </div>
        <div
          css={css`
						display: flex;
						width: 100%;
						${responsiveColumn};

						> div {
							flex: 1;
							display: flex;
							flex-direction: column;
							align-items: center;

							svg {
								vertical-align: middle;
							}
						}
					`}>
          <div>
            <div>
              <span>{t('courses:map.facebook_reviews')}:</span>
              <span
                css={css`
									color: ${theme.highlightColor};
								`}>
                <FaStar />
                <FaStar />
                <FaStar />
                <FaStar />
                <FaStar />
              </span>
            </div>
            <small>(5.0/5.0)</small>
          </div>
          <div>
            <div>
              <span>{t('courses:map.switchup_reviews')}:</span>
              <span
                css={css`
									color: ${theme.highlightColor};
								`}>
                <FaStar />
                <FaStar />
                <FaStar />
                <FaStar />
                <FaStar />
              </span>
            </div>
            <small>(4.92/5.0)</small>
          </div>
          <div>
            <div>
              <span>
                {t('courses:map.coursereport_reviews')}:
              </span>
              <span
                css={css`
									color: ${theme.highlightColor};
								`}>
                <FaStar />
                <FaStar />
                <FaStar />
                <FaStar />
                <FaStar />
              </span>
            </div>
            <small>(4.95/5.0)</small>
          </div>
        </div>
        <H2Highlight>
          {t('courses:common.upcoming_cohorts')}
        </H2Highlight>
        <div
          css={css`
						display: flex;
						justify-content: center;
					`}>
          <CourseMAPLocator />
        </div>
        {cohorts?.map((cohort, i) => (
          <div
            key={i}
            css={css`
							display: flex;
							max-width: 700px;
							margin: 1rem auto;
							font-size: 24px;
							color: #fff;
							padding-bottom: 0.5rem;
							border-bottom: solid 1px rgba(255, 255, 255, 0.2);

							> span {
								display: block;
								color: white;

								&:last-child {
									flex: 1;
								}
							}
						`}>
            <h3
              css={css`
								display: flex;
								flex-direction: column;
								text-align: center;
								padding: 0 1.5rem;
								font-size: 2rem;
								margin: 0;

								small {
									font-size: 0.8rem;
									font-weight: normal;
								}
							`}>
              <Trans
                i18nKey='courses:common.cohort_x'
                values={{
                  cohort: cohort?.value?.cohort!,
                }}>
                <small>Text</small> Text
              </Trans>
            </h3>
            <dl
              css={css`
								margin: 0;

								> dt,
								> dd {
									margin: 0;
								}

								> dt {
									font-size: 0.8rem;
									margin-top: 0.5rem;

									&:first-of-type {
										margin: 0;
									}
								}
							`}>
              <dt>{t('courses:common.start_date')}</dt>
              <dd>
                {cohort?.value?.start_date} (
                {moment(
                  cohort?.value?.start_date,
                  'YYYY-MM-DD'
                ).format('ddd')}
                )
              </dd>
              <dt>{t('courses:common.venue')}</dt>
              <dd
                css={css`
									display: flex;
									flex-direction: column;
									flex-wrap: wrap;
									align-items: flex-start;
									gap: 0.2rem;

									> span {
										padding: 0.2rem;
									}
									> .full {
										background-color: rgba(255, 0, 0, 0.4);
									}
									> .almost {
										background-color: rgba(
											255,
											128,
											0,
											0.4
										);
									}
								`}>
                {[
                  {
                    country: 'hk' as const,
                    location: 'tsuen_wan' as const,
                  },
                  {
                    country: 'hk' as const,
                    location: 'sheung_wan' as const,
                  },
                  {
                    country: 'hk' as const,
                    location: 'kowloon_bay' as const,
                  },
                  {
                    country: 'uk' as const,
                    location: 'london' as const,
                  },
                  {
                    country: 'uk' as const,
                    location: 'uk_online' as const,
                  }
                ]
                  .filter(
                    (venue) =>
                      venue.country === currentCountry
                  )
                  .map(
                    (venue, i) =>
                      cohort?.value?.[venue.location] && (
                        <span
                          key={i}
                          className={cx({
                            full: cohort.value?.[
                              `${venue.location}_full`
                            ],
                            almost: cohort.value?.[
                              `${venue.location}_almost`
                            ],
                          })}>
                          <span
                            className={`fi fi-${venue.country}`}></span>{' '}
                          {t(
                            `courses:common.${venue.location}`
                          )}{' '}
                          {cohort.value?.[
                            `${venue.location}_full`
                          ] &&
                            `(${t(
                              'courses:common.full'
                            )})`}{' '}
                          {cohort.value?.[
                            `${venue.location}_almost`
                          ] &&
                            `(${t(
                              'courses:common.almost_full'
                            )})`}
                        </span>
                      )
                  )}
              </dd>
            </dl>
          </div>
        ))}
        {cohorts?.length === 0 && (
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              height: 200px;
              font-size: 1.5rem;
            `}>
            {t('courses:common.no_cohorts')}
          </div>
        )}
      </div>
      <CourseButtons
        course={course}
        openDay={props?.data.openDayEvent!}
        trial={
          currentCountry === 'hk'
            ? props?.data.trialLessonEvent!
            : props?.data.trialLessonEventUK!
        }
      />
      <div
        css={css`
					display: flex;
					align-items: center;
					justify-content: center;
					flex-wrap: wrap;
					gap: 1rem;
					margin: 1rem;
				`}>
        {currentCountry === 'hk' &&
          props.data.locations.nodes.map((location) =>
            location.gallerySharp?.map((photo, i) => (
              <GatsbyImage
                image={photo.childImageSharp?.gatsbyImageData}
                alt={t('courses:map.photo_of_location', {
                  location: location.name,
                })}
              />
            ))
          )}
      </div>
      <FeaturedBy
        media={props.data.allCockpitMediaLogosCollection.nodes}
      />
      <H2Highlight>
        {t('courses:common.course_cert')}
      </H2Highlight>
      <div css={css`
						display: flex;`}>
        <div
          css={css`
						width:50%;
						display: flex;
						align-items: center;
						justify-content: center;
						flex-wrap: wrap;
						gap: 1rem;
						margin: 1rem;
					`}>
          <GatsbyImage
            image={
              props.data.course?.TQUK_CertificateSharp?.childImageSharp
                ?.gatsbyImageData
            }
            alt={t('courses:map.tquk.tquk_endorsed_course_cert')}
          />
        </div>
        <div
          css={css`
						width:50%;
						display: flex;
						align-items: center;
						justify-content: center;
						flex-wrap: wrap;
						gap: 1rem;
						margin: 1rem;
					`}>
          <GatsbyImage
            image={
              props.data.course?.Tecky_Academy_MicroMaster_CertificateSharp?.childImageSharp
                ?.gatsbyImageData
            }
            alt={t('courses:common.tecky_cert')}
          />
        </div>
      </div>
      <H2Highlight>{t('courses:common.graduates_hired_by')}</H2Highlight>
      <small>{t('courses:common.all_it_related_post')}</small>
      <div
        css={css`
					display: flex;
					flex-wrap: wrap;
					justify-content: center;
				`}>
        {props.data.hiringPartners?.logosSharp?.map((logo, i) => (
          <div
            key={logo.childImageSharp?.id}
            css={css`
							margin: 0.5rem;
							padding: 0.5rem;
							background-color: white;
							border-radius: 1rem;
						`}>
            <ScrollAnimation animateIn='flip'>
              <GatsbyImage
                image={logo.childImageSharp?.gatsbyImageData}
                alt={
                  props.data.hiringPartners?.logos?.[i]?.meta
                    .title
                }
              />
            </ScrollAnimation>
          </div>
        ))}
      </div>
      {currentCountry !== 'uk' && (
        <div
          css={css`
						display: flex;
						width: 100%;
						${responsiveColumn};

						> div {
							flex: 1;
							display: flex;
							flex-direction: column;
							align-items: center;

							> h3 {
								color: ${theme.highlightColor};
							}

							p {
								margin: 1.5rem 0;
								font-size: 4rem;
								text-align: center;

								${maxWidth[2]} {
									font-size: 3rem;
								}
							}
						}
					`}>
          <div>
            <h3>
              {t(
                'courses:map.outcomes.average_time_get_hired.title'
              )}
            </h3>
            <ScrollAnimation animateIn='fadeInDown'>
              <p>
                {t(
                  'courses:map.outcomes.average_time_get_hired.description'
                )}
              </p>
            </ScrollAnimation>
          </div>
          <div>
            <h3>
              {t('courses:map.outcomes.average_salary.title')}
            </h3>
            <ScrollAnimation animateIn='fadeInDown'>
              <p
                css={css`
									small {
										font-size: 0.5rem;
										display: block;
										margin-top: 1rem;
									}
								`}>
                {currentCountry === 'hk' &&
                  t(
                    'courses:map.outcomes.average_salary.description'
                  )}
              </p>
            </ScrollAnimation>
          </div>
        </div>
      )}
      <CourseSummary>
        <ul>
          <li>
            <FaCalendarCheck />
            <em>{t('courses:common.next_start date')}</em>
            <span>{nextCohort?.value?.start_date}</span>
          </li>
          <li>
            <FaClock />
            <em>{t('courses:common.time')}</em>
            <span>
              {currentCountry === 'hk'
                ? course.summary?.time
                : course.summary?.time_uk}
            </span>
          </li>
          <li>
            <FaCalendar />
            <em>{t('courses:common.duration')}</em>
            <span>
              {currentCountry === 'hk'
                ? course.summary?.duration
                : course.summary?.duration_uk}
            </span>
          </li>
          <li>
            <FaPortrait />
            <em>{t('courses:common.class_size')}</em>
            <span>
              {currentCountry === 'hk'
                ? course.summary?.class_size
                : course.summary?.class_size_uk}
            </span>
          </li>
          <li>
            <FaMapMarkerAlt />
            <em>{t('courses:common.location')}</em>
            <span>
              {currentCountry === 'hk'
                ? course.summary?.location
                : course.summary?.location_uk}
            </span>
          </li>
        </ul>
        <ScrollAnimation animateIn='fadeInRight'>
          <GatsbyImage
            css={css`
							margin: 2rem;
						`}
            image={
              course.imageSharp?.childImageSharp?.gatsbyImageData
            }
            alt={course.name}
          />
        </ScrollAnimation>
      </CourseSummary>
      <CourseMAPNav />
    </MAPLayout>
  )
}

export default MAPIndexPage

export const query = graphql`
	fragment MAPCoursePageFragment on Query {
		openDayEvent: eventbrite(
			status: { eq: "live" }
			name: { text: { regex: "/(Open Day|開放)/" } }
			listed: { eq: true }
		) {
			id
			url
			start {
				utc
			}
		}
		openDayEventUK: eventbrite(
			status: { eq: "live" }
			name: { text: { regex: "/(Open Day|開放)/" } }
			listed: { eq: true }
		) {
			id
			url
			start {
				utc
			}
		}
		trialLessonEvent: eventbrite(
			status: { eq: "live" }
			name: { text: { regex: "/香港.*試堂/" } }
			listed: { eq: true }
		) {
			id
			url
			name {
				text
			}
			start {
				utc
			}
		}
		trialLessonEventUK: eventbrite(
			status: { eq: "live" }
			name: { text: { regex: "/英國.*試堂/" } }
			listed: { eq: true }
		) {
			id
			url
			name {
				text
			}
			start {
				utc
			}
		}
		course: cockpitCoursesCollection(
			_lang: { eq: $language }
			code: { eq: "MAP" }
		) {
			name
			mode
			code
			zohoCode
			leadValue
			slogan
			description
			outcome
			standard_price
			summary {
				class_size_uk
				location_uk
				duration_uk
				time_uk
				class_size
				location
				duration
				time
			}
			image {
				path
			}
			imageSharp {
				childImageSharp {
					gatsbyImageData(width: 500, placeholder: BLURRED)
				}
			}
			details {
				title
				mode
				description
				icons {
					meta
				}
				iconsSharp {
					childImageSharp {
						gatsbyImageData(height: 60, placeholder: BLURRED)
					}
				}
				image {
					path
				}
				imageSharp {
					childImageSharp {
						gatsbyImageData(
							transformOptions: { fit: CONTAIN }
							backgroundColor: "transparent"
							height: 400
							width: 400
							placeholder: BLURRED
						)
					}
				}
			}
			leaflet_available
			cohorts {
				value {
					cohort
					price
					price_valid_until
					price_uk
					price_ca
					available_countries
					kowloon_bay
					kowloon_bay_full
					kowloon_bay_almost
					sheung_wan
					sheung_wan_almost
					sheung_wan_full
					start_date
					tsuen_wan
					tsuen_wan_almost
					tsuen_wan_full
					uk_online
					uk_online_almost
					uk_online_full
					london
					london_almost
					london_full
					ca_online
					ca_online_almost
					ca_online_full
					toronto
					toronto_almost
					toronto_full
				}
			}
			instructors {
				name
				description
				title
				specialities
				profileImg {
					path
				}
				available_countries
				profileImgSharp {
					childImageSharp {
						gatsbyImageData(width: 80, placeholder: BLURRED)
					}
				}
			}
			Tecky_Academy_MicroMaster_CertificateSharp {
				childImageSharp {
				gatsbyImageData
				}
			}
			Tecky_Academy_MicroMaster_Certificate {
				path
			}
			TQUK_CertificateSharp {
				childImageSharp {
				gatsbyImageData
				}
			}
			TQUK_Certificate {
				path
			}
		}
	}
	query MAPIndex($language: String!) {
		...TranslationFragment
		...MAPCoursePageFragment
		allCockpitMediaLogosCollection(filter: { _lang: { eq: $language } }) {
			nodes {
				title
				mediaLogo {
					path
				}
				mediaLogoSharp {
					childImageSharp {
						gatsbyImageData(width: 80, placeholder: BLURRED)
					}
				}
			}
		}
		locations: allCockpitLocationsCollection(
			filter: { _lang: { eq: $language } }
			sort: { fields: _o, order: ASC }
		) {
			nodes {
				_id
				country
				name
				gallery {
					path
				}
				gallerySharp {
					childImageSharp {
						gatsbyImageData(width: 300, placeholder: BLURRED)
					}
				}
			}
		}
		tqukLogo: file(relativePath: { eq: "tquk.png" }) {
			childImageSharp {
				gatsbyImageData(width: 100, placeholder: BLURRED)
			}
		}
		hiringPartners: cockpitHiringPartnersCollection(
			_lang: { eq: $language }
		) {
			logos {
				meta
			}
			logosSharp {
				childImageSharp {
					id
					gatsbyImageData(
						width: 80
						height: 80
						placeholder: BLURRED
						backgroundColor: "transparent"
						transformOptions: { fit: CONTAIN }
					)
				}
			}
		}
	}
`
